import { db } from "../../../boot/firebase";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import LibraryFolder from "../../../classes/LibraryFolder";

// Firestore caps a batch at 500 writes.
const BATCH_LIMIT = 400;

export const GET_LIBRARY_FOLDER = ({ dispatch }, id) => {
  return dispatch("GET_DOC", {
    converter: LibraryFolder,
    path: LibraryFolder.getPath(id),
    stateKey: "libraryFolders",
    id,
  });
};

export const GET_LIBRARY_FOLDERS = (
  { dispatch },
  { where, limit, pagination } = {}
) => {
  return dispatch("GET_COLLECTION", {
    converter: LibraryFolder,
    stateKey: "libraryFolders",
    path: LibraryFolder.getPath(),
    where,
    limit,
    pagination,
  });
};

export const LISTEN_LIBRARY_FOLDER = (
  { dispatch },
  { id, next, error, unlisten }
) => {
  return dispatch("LISTEN_DOC", {
    converter: LibraryFolder,
    path: LibraryFolder.getPath(id),
    stateKey: "libraryFolders",
    id,
    next,
    error,
    unlisten,
  });
};

export const LISTEN_LIBRARY_FOLDERS = (
  { dispatch },
  { where, listenerPath, next, error, unlisten } = {}
) => {
  return dispatch("LISTEN_COLLECTION", {
    converter: LibraryFolder,
    path: LibraryFolder.getPath(),
    stateKey: "libraryFolders",
    listenerPath,
    where,
    next,
    error,
    unlisten,
  });
};

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------

// True when `candidateID` is `id` itself or sits anywhere below it. Walks up
// from the candidate, so its ancestors must be loaded
// (ENSURE_LIBRARY_FOLDER_PATH).
export function isSelfOrDescendant(folders, id, candidateID) {
  let cursor = candidateID;
  const visited = new Set();
  while (cursor && !visited.has(cursor)) {
    if (cursor === id) return true;
    visited.add(cursor);
    const next = folders[cursor];
    cursor = next ? next.data.parentID : null;
  }
  return false;
}

// Push the read-only flag onto every folder below `folder` so the rules
// (which read only a folder's own flag) agree with the UI (which walks
// ancestors). The whole subtree, fetched: most of it is never loaded.
async function cascadeReadOnly({ dispatch, state }, folder, readOnly) {
  const { folders } = await dispatch("GET_LIBRARY_SUBTREE", {
    folder,
    withFiles: false,
  });
  const ids = folders.map((descendant) => descendant.id);
  const now = new Date();
  for (let i = 0; i < ids.length; i += BATCH_LIMIT) {
    const batch = writeBatch(db);
    ids.slice(i, i + BATCH_LIMIT).forEach((folderID) => {
      batch.update(doc(db, LibraryFolder.getPath(folderID)), {
        readOnly,
        modified: now,
        modifiedBy: state.user.id,
      });
    });
    await batch.commit();
  }
}

// "Read-only" is only the right message when read-only is the reason; a user
// without access to the tier gets the generic refusal instead.
function assertWritable(state, folder) {
  if (!folder || folder.canBeEditedBy(state.user)) return;
  if (folder.isReadOnly && folder.canBeViewedBy(state.user)) {
    throw "libraryFolderReadOnly";
  }
  throw "libraryNotPermitted";
}

// ----------------------------------------------------------------------------
// Write operations
// ----------------------------------------------------------------------------

export const SAVE_LIBRARY_FOLDER = async ({ dispatch, state }, folder) => {
  if (!folder || !folder.isValid) {
    throw "Folder is not valid";
  }

  const data = { ...folder.data };
  const now = new Date();
  const folders = state.libraryFolders || {};

  if (folder.id) {
    // Update existing
    const existing = folders[folder.id];
    if (!existing) throw "libraryFolderNotFound";
    assertWritable(state, existing);
    const readOnlyChanged =
      Boolean(data.readOnly) !== Boolean(existing.data.readOnly);
    if (readOnlyChanged && !existing.canBeLockedBy(state.user)) {
      throw "libraryFolderReadOnly";
    }
    await updateDoc(doc(db, LibraryFolder.getPath(folder.id)), {
      name: data.name,
      parentID: data.parentID,
      tier: data.tier,
      description: data.description,
      readOnly: Boolean(data.readOnly),
      modified: now,
      modifiedBy: state.user.id,
    });
    if (readOnlyChanged) {
      await cascadeReadOnly(
        { dispatch, state },
        existing,
        Boolean(data.readOnly)
      );
    }
    dispatch("LOG_LIBRARY_ACTIVITY", {
      action: "folder.edit",
      subjectType: "folder",
      subjectID: folder.id,
      subjectName: data.name,
      tier: data.tier,
      details: readOnlyChanged ? { readOnly: Boolean(data.readOnly) } : null,
    });
    return folder.id;
  }

  // Create new. The parent may be a folder created a moment ago by a dropped
  // tree, or one no listener holds: fetch it, and the chain above it that
  // its read-only flag inherits from.
  if (data.parentID && !folders[data.parentID]) {
    await dispatch("ENSURE_LIBRARY_FOLDER_PATH", data.parentID).catch(
      () => undefined
    );
  }
  const parent = data.parentID
    ? state.libraryFolders[data.parentID] || null
    : null;
  if (data.parentID && !parent) throw "libraryFolderNotFound";
  if (!LibraryFolder.canBeCreatedIn(state.user, parent, data.tier)) {
    throw parent && parent.isReadOnly && parent.canBeViewedBy(state.user)
      ? "libraryFolderReadOnly"
      : "libraryNotPermitted";
  }
  if (parent) {
    // Subfolders inherit their parent's tier and read-only flag.
    data.tier = parent.data.tier;
    data.readOnly = Boolean(data.readOnly) || parent.isReadOnly;
  }
  if (data.readOnly && !(state.user && state.user.isAdmin)) {
    throw "libraryNotPermitted";
  }
  data.readOnly = Boolean(data.readOnly);
  data.created = now;
  data.createdBy = state.user.id;
  data.modified = now;
  data.modifiedBy = state.user.id;
  const ref = await addDoc(collection(db, LibraryFolder.getPath()), data);
  dispatch("LOG_LIBRARY_ACTIVITY", {
    action: "folder.create",
    subjectType: "folder",
    subjectID: ref.id,
    subjectName: data.name,
    tier: data.tier,
    details: { parentID: data.parentID },
  });
  return ref.id;
};

export const MOVE_LIBRARY_FOLDER = async (
  { dispatch, state },
  { id, parentID }
) => {
  if (!id) throw "Folder ID is required";
  const folder = (state.libraryFolders || {})[id];
  if (!folder) throw "libraryFolderNotFound";
  assertWritable(state, folder);

  const newParentID = parentID || null;
  if ((folder.data.parentID || null) === newParentID) return;

  let destination = null;
  if (newParentID) {
    // The self/descendant check walks up from the destination.
    await dispatch("ENSURE_LIBRARY_FOLDER_PATH", newParentID).catch(
      () => undefined
    );
    const folders = state.libraryFolders || {};
    // Disallow moving a folder into itself or any of its descendants.
    if (isSelfOrDescendant(folders, id, newParentID)) {
      throw "libraryMoveIntoSelf";
    }

    // The destination folder must be in the same tier as the folder being
    // moved. Cross-tier moves would require re-uploading every file under a
    // different storage prefix and would silently change access; keep it
    // simple by requiring the user to do that explicitly.
    destination = folders[newParentID];
    if (!destination) throw "libraryFolderNotFound";
    if (destination.data.tier !== folder.data.tier) {
      throw "libraryMoveCrossTier";
    }
    assertWritable(state, destination);
  }

  const now = new Date();
  await updateDoc(doc(db, LibraryFolder.getPath(id)), {
    parentID: newParentID,
    modified: now,
    modifiedBy: state.user.id,
  });
  // A folder dropped into a read-only tree becomes read-only with it.
  if (destination && destination.isReadOnly && !folder.data.readOnly) {
    await updateDoc(doc(db, LibraryFolder.getPath(id)), { readOnly: true });
    await cascadeReadOnly({ dispatch, state }, folder, true);
  }
  dispatch("LOG_LIBRARY_ACTIVITY", {
    action: "folder.move",
    subjectType: "folder",
    subjectID: id,
    subjectName: folder.data.name,
    tier: folder.data.tier,
    details: { from: folder.data.parentID || null, to: newParentID },
  });
};

export const DELETE_LIBRARY_FOLDER = async (
  { commit, dispatch, state },
  id
) => {
  if (!id) return;
  const root = (state.libraryFolders || {})[id];
  if (!root) throw "libraryFolderNotFound";
  assertWritable(state, root);

  // The folder and everything below it, fetched: most of a subtree is never
  // loaded, and a folder deleted without its subfolders would strand them.
  const subtree = await dispatch("GET_LIBRARY_SUBTREE", { folder: root });
  const folders = { [id]: root };
  subtree.folders.forEach((folder) => (folders[folder.id] = folder));
  const folderIds = [id, ...subtree.folders.map((folder) => folder.id)];

  // Trash every file inside any of these folders. Each DELETE_LIBRARY_FILE
  // is a soft delete, so the files can still be restored from Trash.
  const fileIds = subtree.files.filter((f) => !f.isDeleted).map((f) => f.id);
  await Promise.all(
    fileIds.map((fid) =>
      dispatch("DELETE_LIBRARY_FILE", fid).catch((err) => {
        console.error("Failed to delete library file", fid, err);
      })
    )
  );

  // Delete folder docs, deepest first so a failure part-way through never
  // strands a child whose parent is already gone.
  await Promise.all(
    folderIds.reverse().map((fid) => {
      const deletedFolder = folders[fid];
      return deleteDoc(doc(db, LibraryFolder.getPath(fid)))
        .then(() => {
          commit("REMOVE", { stateKey: "libraryFolders", id: fid });
          dispatch("LOG_LIBRARY_ACTIVITY", {
            action: "folder.delete",
            subjectType: "folder",
            subjectID: fid,
            subjectName: deletedFolder ? deletedFolder.data.name : "",
            tier: deletedFolder ? deletedFolder.data.tier : null,
          });
        })
        .catch((err) => {
          console.error("Failed to delete library folder", fid, err);
        });
    })
  );
};
